import {
  DataResult,
  State as DataState,
  FilterOperator,
} from '@progress/kendo-data-query';
import {
  GridCellProps,
  GridDataStateChangeEvent,
} from '@progress/kendo-react-grid';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProductStatusTypes } from '../../../../../common/enums';
import useMount from '../../../../../common/hooks/useMount';
import { DataLoader } from '../../../../../common/widgets/DataLoader';
import Grid, { GridCellTd, getSkip } from '../../../../../common/widgets/Grid';
import Icons from '../../../../../common/widgets/Icons';
import Loader from '../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import { config } from '../../../config';
import { productStatusColor } from '../../../constants';
import { ProductTypes } from '../../../enums';
import { ProductItemModel, ProductStatusModel } from '../../../models/products';
import { resources } from '../../../resources/resources';
import { productsService } from '../../../services/products';
import { EmptyStateNoProducts } from '../../Common/EmptyStates';
import { getStockCell } from '../Common/List';

const initialDataState: DataState = {
  skip: 0,
  take: config.grid.defaultPageSize,
  sort: [{ field: 'name', dir: 'asc' }],
  filter: {
    logic: 'and',
    filters: [
      {
        field: 'statusId',
        operator: FilterOperator.EqualTo,
        value: ProductStatusTypes.Active,
      },
      {
        logic: 'or',
        filters: [
          {
            field: 'productTypeId',
            operator: FilterOperator.EqualTo,
            value: ProductTypes.ProductOrService,
          },
          {
            field: 'productTypeId',
            operator: FilterOperator.EqualTo,
            value: ProductTypes.GiftCard,
          },
        ],
      },
    ],
  },
};

export const AddCodesList = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const odataString = searchParams.get('dataState');
  const [products, setProducts] = useState<ProductItemModel[] | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [dataState, setDataState] = useState<DataState>(
    odataString ? JSON.parse(decodeURI(odataString)) : initialDataState,
  );
  const [showErrorPanel, setShowErrorPanel] = useState<boolean | undefined>();
  const [statuses, setStatuses] = useState<ProductStatusModel[] | undefined>();

  const isLoading = _.isNil(statuses) || _.isNil(products);

  const loadStatuses = async (abortController?: AbortController) => {
    const resp = await productsService.getProductStatuses({
      signal: abortController?.signal,
    });
    if (abortController?.signal.aborted) return;
    if (resp.hasErrors) {
      setShowErrorPanel(true);
    } else {
      setStatuses(resp.data);
    }
  };

  useMount(loadStatuses);

  const dataStateChange = (e: GridDataStateChangeEvent) => {
    navigate(
      `/products/codes?dataState=${encodeURI(
        JSON.stringify({
          ...e.dataState,
          skip: getSkip(dataState, e.dataState),
        }),
      )}`,
    );
  };

  useEffect(() => {
    setDataState(
      odataString ? JSON.parse(decodeURI(odataString)) : initialDataState,
    );
  }, [odataString]);

  const onDataReceived = (data: DataResult, showErrorPanel?: boolean) => {
    setProducts(data.data);
    setTotal(data.total);
    setShowErrorPanel(showErrorPanel);
  };

  return (
    <div className="m-products m-all-products">
      <div className="m-panel new-codes">
        <h1>{translate(resources.products.list.codes.title)}</h1>
        <p>{translate(resources.products.list.codes.subtitle)}</p>
        <DataLoader
          getData={productsService.getProducts}
          dataState={dataState}
          onDataReceived={onDataReceived}
        />
        <Loader
          loading={isLoading}
          loader={<LoadingSpinner />}
          showErrorPanel={showErrorPanel}
        >
          {!!products && !!statuses && (
            <>
              {products.length ? (
                <div className="m-grid m-products-grid">
                  <Grid
                    data={products}
                    pageable={{
                      pageSizes: config.grid.pageSizes,
                      pageSizeValue: dataState.take,
                      info: false,
                      previousNext: true,
                    }}
                    {...dataState}
                    onDataStateChange={dataStateChange}
                    total={total}
                    sortable={{ allowUnsort: true, mode: 'multiple' }}
                    columns={[
                      {
                        field: 'name',
                        title: translate(resources.products.list.columns.name),
                      },
                      {
                        field: 'status',
                        title: translate(
                          resources.products.list.columns.status,
                        ),
                        cell: ({ dataItem }: GridCellProps) => (
                          <GridCellTd className="grid-center">
                            <span
                              style={{
                                backgroundColor:
                                  productStatusColor[
                                    dataItem.statusId as ProductStatusTypes
                                  ],
                              }}
                              className="m-grid-badge"
                            >
                              {dataItem.status}
                            </span>
                          </GridCellTd>
                        ),
                        headerClassName: 'grid-center',
                      },
                      {
                        field: 'categories',
                        title: translate(
                          resources.products.list.columns.categories,
                        ),
                        sortable: false,
                      },
                      {
                        field: 'productType',
                        title: translate(
                          resources.products.list.columns.productType,
                        ),
                      },
                      {
                        field: 'stock',
                        title: translate(resources.products.list.columns.stock),
                        cell: ({ dataItem }: GridCellProps) =>
                          getStockCell(dataItem),
                        headerClassName: 'grid-center',
                      },

                      {
                        cell: ({ dataItem }) => (
                          <GridCellTd className="grid-center">
                            <button
                              onClick={() =>
                                navigate(
                                  `/products/add/codes/${dataItem.productId}`,
                                )
                              }
                              className="btn-link"
                            >
                              <span className="btn-icon">
                                <Icons.ArrowButton />
                              </span>
                              <span className="btn-text">
                                {translate(
                                  resources.products.list.codes.chooseProduct,
                                )}
                              </span>
                            </button>
                          </GridCellTd>
                        ),
                        headerClassName: 'grid-center',
                      },
                    ]}
                  />
                </div>
              ) : (
                <EmptyStateNoProducts />
              )}
            </>
          )}
        </Loader>
      </div>
    </div>
  );
};
